import React, { MouseEvent, useMemo } from 'react'
import cx from 'classnames'
import get from 'lodash/get'
import { l } from '@manychat/manyui'

import { AddCondition } from 'common/filter/components/AddCondition'
import { Condition } from 'common/filter/components/Condition'
import { GLOBAL_OPERATORS } from 'common/filter/constants'
import { FilterCondition } from 'common/filter/interfaces'

import { useFilterContext } from './FilterContext'
import { FilterGroupProps } from './types'

import cm from './Filters.module.css'

export const FilterGroupItem = ({
  disabled,
  theme,
  group,
  groupIndex,
  dataTestId,
  setOperatorPopoverAnchor,
}: FilterGroupProps) => {
  const filter = useFilterContext()
  const { config } = filter
  const { groups, operator } = filter.value
  const multi = groups.length > 1 && !config.isHiddenFirstGroup
  const isDisabled = disabled || config.readOnlyGroupIndexes?.includes(groupIndex)

  const groupOpLabel = l.getString(
    (GLOBAL_OPERATORS.find((operator) => operator.value === group.operator) || {}).label,
  )

  const groupCn = cx({
    [cm.multiGroup]: multi,
    [cm.firstGroup]: groupIndex === 0,
    [cm.lastGroup]: groupIndex === groups.length - 1,
    [cm.disabledGroup]: isDisabled,
  })

  const usedFields = useMemo(
    () =>
      group.items.reduce<Record<string, string | undefined>>((fieldsMap, condition) => {
        if (condition.field) {
          fieldsMap[condition.field] = condition.operator
        }
        return fieldsMap
      }, {}),
    [group],
  )

  const handleOperatorPopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setOperatorPopoverAnchor(event.currentTarget as HTMLElement)
  }

  return (
    <div key={groupIndex} className={cx(groupCn, theme.groupCn)}>
      {multi && groupIndex < groups.length - 1 && (
        <div className={cm.globalOperator}>{operator}</div>
      )}
      {!config.operatorSelectorAlwaysHidden && (
        <>
          {disabled ? (
            <>
              {(config.operatorSelectorAlwaysVisible || group.items.length > 0) && (
                <div className={cx(cm.operatorSelector, theme.operatorSelector)}>
                  <strong>
                    {multi
                      ? l.translate('Targeted contacts, that match ')
                      : l.translate('Targeted only contacts, that match ')}
                    <span>{groupOpLabel}:</span>
                  </strong>
                </div>
              )}
            </>
          ) : (
            <>
              {(config.operatorSelectorAlwaysVisible || group.items.length > 0) && (
                <div
                  className={cx(theme.operatorSelector, {
                    [cm.operatorSelector]: !config.isConditionBuilder && !config.caption,
                  })}
                >
                  <span className={cx('popover-form-label m-0', theme.label)}>
                    {config.caption ? (
                      `${config.caption}`
                    ) : (
                      <>
                        {config.isConditionBuilder ? (
                          <span>{l.translate('Does the contact match')}</span>
                        ) : (
                          <>
                            {multi ? (
                              <span>{l.translate('Contacts, that match ')}</span>
                            ) : (
                              <span>{l.translate('Only contacts, that match ')}</span>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </span>
                  <a
                    onClick={handleOperatorPopoverOpen}
                    className={cx(
                      cm.popoverTrigger,
                      {
                        'text-primary p-t-0 text-subheading text-normal':
                          config.isConditionBuilder || config.caption,
                        [cm.darkText]: !config.isConditionBuilder && !config.caption,
                      },
                      theme.popoverTrigger,
                    )}
                    data-index={groupIndex}
                  >
                    {groupOpLabel}
                    {!config.caption && config.isConditionBuilder ? '?' : ':'}
                  </a>
                </div>
              )}
            </>
          )}
        </>
      )}
      <div className={cx(theme.conditionsWrapper)}>
        <div className={cx(cm.conditions, theme.conditions)}>
          {group.items.map((item, index) => {
            const disabledItem = config.readOnlyItemIndexesByGroup?.[groupIndex]?.includes(index)
            const key =
              '_oid' in item && typeof item._oid === 'string' && item._oid
                ? item._oid
                : `${String(item.type)}-${String(item.value)}`

            return (
              <Condition
                key={key}
                condition={item as FilterCondition}
                usedFields={usedFields}
                groupIndex={groupIndex}
                disabled={disabledItem || disabled}
                className={theme.condition}
                summaryClassName={theme.summary}
              />
            )
          })}
          {!disabled && !(config.onlyOneCondition && get(groups, '0.items.0')) && (
            <AddCondition
              data-test-id={dataTestId}
              className={theme.addConditionBtn}
              firstClassName={theme.addFirstConditionBtn}
              label={config.addConditionLabel}
              first={group.items.length < 1}
              groupIndex={groupIndex}
            />
          )}
        </div>
        <div className={theme.smartSegmentWrapper}>{config.renderSmartSegmentControl()}</div>
      </div>
    </div>
  )
}
